import { LeftOutlined } from '@ant-design/icons'
import { Button, Flex, message, Modal } from 'antd'
import React, { useMemo, useState } from 'react'
import { useUserContext } from '~/core/context'
import { Api } from '~/core/trpc'
import { AppSessionType } from '~/features/appSession/common'
import { FormConfigurationType } from '~/features/formConfiguration/common'
import { StepType } from '../../../common/types'
import { Viewer } from '../Viewer'
import { FlowOverview } from './components/FlowOverview'

type Props = {
  appSessionId: string
  onCancel: () => void
  onSubmitted: (appSession: AppSessionType) => void
}

export const FormConfigurationModalFlows: React.FC<Props> = ({
  appSessionId,
  onCancel,
  onSubmitted,
}) => {
  const { session } = useUserContext()

  const [formConfiguration, setFormConfigurationSelected] =
    useState<FormConfigurationType>()

  const handleClick = (item: FormConfigurationType) => {
    setFormConfigurationSelected(item)
  }

  const updateMutation = Api.appSession.update.useMutation()
  const triggerEventMutation = Api.dataConnector.triggerEvent.useMutation()

  const appSessionQuery = Api.appSession.findUnique.useQuery({
    where: { id: appSessionId },
  })

  const appSession = appSessionQuery.data as AppSessionType

  const formConfigurationsQuery = Api.formConfiguration.findMany.useQuery(
    {
      where: { appId: appSession?.appId, type: 'FLOW' },
    },
    {
      enabled: !!appSession?.appId,
      initialData: [],
    },
  )

  const formConfigurations =
    formConfigurationsQuery.data as FormConfigurationType[]

  const formValues = useMemo(() => {
    return appSession?.values?.forms?.[formConfiguration?.key]
  }, [appSession?.values, formConfiguration])

  const valuesConfiguration = {
    appId: appSession?.appId,
    appSessionId: appSession?.id,
    timezone: session.user.timezone,
  }

  const triggerHttpEvent = async (eventKey: string) => {
    await triggerEventMutation.mutateAsync({
      appId: appSession.appId,
      providerKey: 'http',
      providerTriggerKey: 'NewRequest',
      appSessionId: appSession.id,
      payload: {
        eventKey,
      },
    })
  }

  const handleChange = async (formValues: any, step?: StepType) => {
    try {
      const valuesUpdated = {
        ...appSession.values,
        forms: {
          ...appSession.values.forms,
          [formConfiguration.key]: formValues,
        },
      }

      const appSessionUpdated = await updateMutation.mutateAsync({
        where: { id: appSession.id },
        data: { values: valuesUpdated },
      })

      if (step) {
        await triggerHttpEvent(
          `flow.${formConfiguration.key}.${step.key}.completed`,
        )
      }

      return appSessionUpdated as AppSessionType
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const handleChangeFromFlow = async (
    formConfiguration: FormConfigurationType,
    itemUpdated: AppSessionType,
    status: 'submitted' | 'disabled',
  ) => {
    try {
      await updateMutation.mutateAsync({
        where: { id: appSession.id },
        data: { values: itemUpdated.values },
      })

      if (status === 'submitted') {
        await triggerHttpEvent(`flow.${formConfiguration.key}.form.completed`)
      } else {
        await triggerHttpEvent(`flow.${formConfiguration.key}.form.disabled`)
      }

      await appSessionQuery.refetch()
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const handleSubmit = async (formValues: any, step: StepType) => {
    const appSessionSubmitted = await handleChange(formValues, step)

    await triggerHttpEvent(`flow.${formConfiguration.key}.form.completed`)

    await formConfigurationsQuery.refetch()

    await appSessionQuery.refetch()

    setFormConfigurationSelected(null)
  }

  return (
    <Modal
      open={true}
      onCancel={() => onSubmitted(appSession)}
      footer={false}
      width={{
        xs: '90%',
        sm: '80%',
        md: '70%',
        lg: '60%',
        xl: '50%',
        xxl: '40%',
      }}
      styles={{
        body: {
          minHeight: '50vh',
          maxHeight: '80vh',
          height: '100%',
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        content: {
          padding: 0,
        },
      }}
    >
      <Flex
        vertical
        className="w-full"
        flex={1}
        style={{ overflowY: 'hidden' }}
      >
        {formConfiguration && (
          <Flex className="pl-2 pt-2">
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => setFormConfigurationSelected(null)}
            >
              Back
            </Button>
          </Flex>
        )}

        <Flex
          vertical
          flex={1}
          className="px-4 pt-4"
          style={{ overflowY: 'auto' }}
        >
          {formConfiguration ? (
            <>
              <Viewer
                values={formValues}
                valuesConfiguration={valuesConfiguration}
                formConfiguration={formConfiguration}
                onChange={handleChange}
                onSubmitForm={handleSubmit}
                onSubmitStep={handleChange}
              />
            </>
          ) : (
            <>
              <FlowOverview
                appSession={appSession}
                formConfigurations={formConfigurations}
                onClick={handleClick}
                onChange={handleChangeFromFlow}
              />
            </>
          )}
        </Flex>
      </Flex>
    </Modal>
  )
}
